/**
 * Socrates Legacy API Client
 * 기존 소크라테스 시스템의 API 호출을 래핑
 * 하이브리드 흐름에서 한교파 API와 함께 사용됨
 */

import { backIncstance } from '../utils/commonFunctions';

// 레거시 API URLs
const URLS = {
  EDIT_GROUP_LIST: process.env.REACT_APP_SOCRATES_EDIT_GROUP_LIST,
  GET_FORM_LIST: process.env.REACT_APP_SOCRATES_GET_FORM,
  START_INTERVIEW: process.env.REACT_APP_SOCRATES_STRAT_INTERVIEW,
};

class SocratesLegacyAPI {
  /**
   * 소크라테스 그룹 생성
   * @param {Object} params
   * @param {string} params.title - 그룹 제목
   * @param {string|number} [params.hgpInterviewId] - 한교파 인터뷰 ID (매핑용, string으로 변환됨)
   * @returns {Promise<{id: number}>}
   */
  async createGroup({ title, hgpInterviewId }) {
    const reqData = {
      title: title || '새 대화',
      // 한교파 인터뷰 ID 매핑 (string)
      ...(hgpInterviewId && { hgp_interview_id: String(hgpInterviewId) }),
    };

    const response = await backIncstance('POST', URLS.EDIT_GROUP_LIST, reqData);
    return this.handleResponse(response);
  }

  /**
   * 소크라테스 폼 생성
   * @param {Object} params
   * @param {number} params.groupId - 그룹 ID
   * @param {string} [params.fileContent] - 파일 내용
   * @param {string} [params.clarioPrompt] - 클라리오 프롬프트
   * @param {string} [params.style] - 스타일
   * @param {string} [params.likeTopic] - 관심 주제
   * @param {string} [params.expTitle] - 면접 제목
   * @param {number} [params.mainMaxCount] - 주요 질문 수
   * @param {number} [params.fuMaxCount] - 후속 질문 수
   * @param {boolean} [params.showResult] - 결과 표시 여부
   * @param {string} params.userId - 사용자 ID
   * @param {string} [params.persona] - 페르소나
   * @param {string|number} [params.hgpInterviewId] - 한교파 인터뷰 ID (string으로 변환됨)
   * @returns {Promise<Object>}
   */
  async createForm({
    groupId,
    fileContent,
    clarioPrompt,
    style,
    likeTopic,
    expTitle,
    mainMaxCount = 10,
    fuMaxCount,
    showResult,
    userId,
    persona = '선생님 말투',
    hgpInterviewId,
  }) {
    const reqData = {
      group_id: groupId,
      file_content: fileContent,
      clario_prompt: clarioPrompt,
      style,
      like_topic: likeTopic,
      exp_title: expTitle,
      is_completed: false,
      main_max_count: mainMaxCount,
      fu_max_count: fuMaxCount,
      show_result: showResult,
      user_id: userId,
      persona,
      // 한교파 인터뷰 ID 포함 (string)
      ...(hgpInterviewId && { hgp_interview_id: String(hgpInterviewId) }),
    };

    const response = await backIncstance('POST', URLS.GET_FORM_LIST, reqData);
    return this.handleResponse(response);
  }

  /**
   * 면접 시작
   * @param {number} groupId - 그룹 ID
   * @returns {Promise<Object>}
   */
  async startInterview(groupId) {
    const reqData = {
      group_id: Number(groupId),
    };

    const response = await backIncstance('POST', URLS.START_INTERVIEW, reqData);
    return this.handleResponse(response);
  }

  /**
   * 공통 응답 처리
   * @private
   */
  async handleResponse(response) {
    if (!response) {
      throw new Error('네트워크 요청 실패');
    }

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || '요청 실패';
      throw new Error(`${errorMessage} (${response.status})`);
    }

    return data;
  }
}

export default new SocratesLegacyAPI();
